import { FormEvent, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useResetPasswordMutation } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [reset, { isLoading }] = useResetPasswordMutation();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    try {
      await reset({ token, password }).unwrap();
      toast.success("Password reset — sign in with the new one.");
      navigate("/login", { replace: true });
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Reset failed";
      setError(msg);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg text-fg p-4">
        <Alert tone="danger">
          Missing token. <Link to="/forgot-password" className="text-accent underline">Request a new link</Link>.
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-bg text-fg p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Choose a new password</CardTitle>
          <CardDescription>The token expires 1 hour after it was issued.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3">
            <Input
              type="password"
              placeholder="New password (min 8 chars)"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            <Input
              type="password"
              placeholder="Confirm new password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {error && <Alert tone="danger">{error}</Alert>}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isLoading || !password}
            >
              {isLoading ? "Saving…" : "Set new password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
